import {
  ButtonIcon,
  ButtonPrimary,
  ButtonIconFill,
  ButtonSecondary
} from './Button.styles';
import { pushEvent } from '@CS/hooks';
import type { MouseEvent } from 'react';
import type { ButtonHTMLAttributes } from 'react';

type ClickEvent = MouseEvent<HTMLButtonElement>;
type Children<Child> = Child | Child[];
type Child = JSX.Element | string;

enum Variants {
  'icon' = 'icon',
  'primary' = 'primary',
  'iconFill' = 'iconFill',
  'secondary' = 'secondary'
}

type IVariant = keyof typeof Variants;

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  onClick?: (event: ClickEvent) => unknown;
  type?: 'button' | 'submit' | 'reset';
  children?: Children<Child>;

  gtmConfig?: object;
  disabled?: boolean;
  variant?: IVariant;

  margin?: string;
  width?: string;
  link?: string;
  text?: string;
  id?: string;
};

const getVariant = (variant?: IVariant) => {
  switch (variant) {
    case Variants.secondary:
      return ButtonSecondary;

    case Variants.icon:
      return ButtonIcon;

    case Variants.iconFill:
      return ButtonIconFill;

    default:
      return ButtonPrimary;
  }
};

export const Button = ({
  variant = Variants.primary,
  disabled = false,
  width = '100%',
  text = '',
  gtmConfig,
  children,
  onClick,
  link,
  id,
  ...props
}: ButtonProps) => {
  const onButtonClick = (event: ClickEvent) => {
    gtmConfig && pushEvent(gtmConfig);
    link ? (window.location.href = link) : onClick?.(event);
  };

  const ButtonStyled = getVariant(variant);

  return (
    <ButtonStyled
      onClick={onButtonClick}
      disabled={disabled}
      width={width}
      {...props}
      id={id}
    >
      {text || children}
    </ButtonStyled>
  );
};
